import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

const GROUPS: { titleKey: string; items: { keys: string; labelKey: string }[] }[] = [
  {
    titleKey: 'shortcuts.group.actions',
    items: [
      { keys: 'n', labelKey: 'shortcuts.newTask' },
      { keys: 'x', labelKey: 'shortcuts.toggle' },
      { keys: 't', labelKey: 'shortcuts.deferTomorrow' },
      { keys: 'j / k', labelKey: 'shortcuts.move' },
      { keys: 'Esc', labelKey: 'shortcuts.clear' },
    ],
  },
  {
    titleKey: 'shortcuts.group.navigation',
    items: [
      { keys: 'Cmd/Ctrl K', labelKey: 'search.title' },
      { keys: 'g t', labelKey: 'nav.today' },
      { keys: 'g w', labelKey: 'nav.week' },
      { keys: 'g i', labelKey: 'nav.inbox' },
      { keys: 'g c', labelKey: 'nav.goals' },
      { keys: 'g s', labelKey: 'nav.settings' },
      { keys: '?', labelKey: 'shortcuts.help' },
    ],
  },
];

export function ShortcutsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) {
      returnFocus.current = document.activeElement as HTMLElement | null;
      // showModal сам ставит ловушку фокуса и обрабатывает Esc.
      el.showModal();
    } else if (!open && el.open) {
      el.close();
      returnFocus.current?.focus();
    }
  }, [open]);

  return (
    <dialog
      ref={dialog}
      aria-labelledby="shortcuts-title"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialog.current) onClose();
      }}
      className="m-auto w-[min(30rem,calc(100vw-2rem))] rounded-card border border-line bg-surface p-6 text-ink backdrop:bg-black/40"
    >
      <div className="flex items-start justify-between gap-4">
        <h2 id="shortcuts-title" className="font-serif text-xl font-semibold">
          {t('shortcuts.title')}
        </h2>
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>

      {GROUPS.map((group) => (
        <section key={group.titleKey} className="mt-5">
          <h3 className="text-xs uppercase tracking-wider text-muted">{t(group.titleKey)}</h3>
          <dl className="mt-2 flex flex-col gap-1.5">
            {group.items.map((item) => (
              <div key={item.keys} className="flex items-baseline justify-between gap-4 text-sm">
                <dt className="text-ink-2">{t(item.labelKey)}</dt>
                <dd className="font-mono text-xs text-muted">{item.keys}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </dialog>
  );
}
